import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LayoutTemplate, Loader2 } from "lucide-react";

const CATS = [["all", "Alles"], ["event", "Events"], ["webinar", "Webinars"], ["onboarding", "Onboarding"]];

export default function MailGallery({ onPick, testId = "mail-gallery" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("all");
  const [preview, setPreview] = useState(null); // {id, html}
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (open && items === null) {
      api.get("/email-gallery").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    }
  }, [open, items]);

  const show = async (t) => {
    setBusy(t.id);
    try {
      const { data } = await api.post("/workflows/email-preview", { subject: t.subject, body: t.body });
      setPreview({ id: t.id, html: data.html });
    } catch { /* stil */ }
    finally { setBusy(""); }
  };

  const filtered = (items || []).filter((t) => cat === "all" || t.category === cat);

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`${testId}-btn`}>
        <LayoutTemplate className="h-4 w-4" /> Galerij
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">Mailontwerpen — kies een startpunt</DialogTitle>
          </DialogHeader>
          <div className="flex gap-1.5">
            {CATS.map(([k, label]) => (
              <button key={k} onClick={() => setCat(k)} data-testid={`${testId}-cat-${k}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${cat === k ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {items === null && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
            {filtered.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 p-3" data-testid={`${testId}-item-${t.id}`}>
                <p className="text-sm font-semibold text-slate-800">{t.name}</p>
                <p className="mt-0.5 text-xs text-slate-500">{t.description}</p>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => show(t)}
                    disabled={busy === t.id} data-testid={`${testId}-view-${t.id}`}>
                    {busy === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Bekijk"}
                  </Button>
                  <Button size="sm" className="h-7 text-xs" data-testid={`${testId}-use-${t.id}`}
                    onClick={() => { onPick({ subject: t.subject, body: t.body }); setOpen(false); setPreview(null); }}>
                    Gebruik dit ontwerp
                  </Button>
                </div>
                {preview?.id === t.id && (
                  <iframe title={`Voorbeeld ${t.name}`} srcDoc={preview.html} sandbox=""
                    className="mt-2 h-72 w-full rounded-lg border border-slate-200 bg-white"
                    data-testid={`${testId}-preview-${t.id}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">
            Tags zoals {"{voornaam}"} en {"{eventnaam}"} worden automatisch ingevuld. Pas na het kiezen de tekst
            en de knop-link aan; de knopkleur volgt de huisstijl (Admin → Mail-opmaak).
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
